import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Input, Select, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { DateRangePicker as ZhDateRangePicker, type TableColumnProps, type TableRef } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { AdminDetailTable } from "@/views/components/DetailTable";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";
import CustomerSelectedDialog, { type CustomerSelectedQuery } from "./CustomerSelectedDialog";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { showRequestError } from "@/utils/requestErrorMessage";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

export interface CustomerInfoRecord {
    customer: {
        id: number;
        code?: string;
        realName?: string;
        institutionName?: string;
        mobile?: string;
        createdTime?: string;
        memberLevel?: number | string;
        memberActiveTime?: string;
        memberExpiryDate?: string;
        memberState?: number | string;
        state?: number | string;
    };
    /** 旧 Vue 列表优先展示机构认证申请中的机构名称。 */
    institutionApply?: {
        institutionName?: string;
    };
    isConcat?: boolean;
    latestCommunicateSummary?: string;
}

export interface CustomerInfoQuery {
    page: number;
    size: number;
    param?: string;
    memberLevel?: number[];
    memberState?: number;
    isContact?: boolean;
    state?: number[];
    sortKey?: string;
    sortOrder?: string;
    registeredBeginTime?: string;
    registeredEndTime?: string;
    activeBeginTime?: string;
    activeEndTime?: string;
    expiryBeginDate?: string;
    expiryEndDate?: string;
}

export interface CustomerInfoPageProps {
    loadCustomers?: (query: CustomerInfoQuery) => Promise<{ list?: CustomerInfoRecord[]; total?: number }>;
    loadCommunicateCount?: (query: CustomerSelectedQuery) => Promise<number>;
    updateState?: (id: number, state: number) => Promise<unknown>;
    restore?: (id: number) => Promise<unknown>;
    exportCustomer?: (query: CustomerSelectedQuery) => Promise<unknown>;
    exportCommunication?: (query: CustomerSelectedQuery) => Promise<unknown>;
    navigateTo?: (id: number) => void;
}

/** 把 Vue 列表的已应用筛选条件编码到 URL，详情返回时可以恢复同一查询上下文。 */
export function serializeCustomerInfoQuery(query: CustomerInfoQuery): string {
    const params = new URLSearchParams();
    if (query.page !== 1) params.set("page", String(query.page));
    if (query.size !== 10) params.set("size", String(query.size));
    if (query.param) params.set("param", query.param);
    query.memberLevel?.forEach(value => params.append("memberLevel", String(value)));
    if (query.memberState != null) params.set("memberState", String(query.memberState));
    if (query.isContact != null) params.set("isContact", String(query.isContact));
    query.state?.forEach(value => params.append("state", String(value)));
    if (query.sortKey) params.set("sortKey", query.sortKey);
    if (query.sortOrder) params.set("sortOrder", query.sortOrder);
    if (query.registeredBeginTime) params.set("registeredBeginTime", query.registeredBeginTime);
    if (query.registeredEndTime) params.set("registeredEndTime", query.registeredEndTime);
    if (query.activeBeginTime) params.set("activeBeginTime", query.activeBeginTime);
    if (query.activeEndTime) params.set("activeEndTime", query.activeEndTime);
    if (query.expiryBeginDate) params.set("expiryBeginDate", query.expiryBeginDate);
    if (query.expiryEndDate) params.set("expiryEndDate", query.expiryEndDate);
    return params.toString();
}

/** 从列表 URL 恢复 Vue 查询参数，非法页码和值回退到旧版默认值。 */
export function parseCustomerInfoQuery(search: string): CustomerInfoQuery {
    const params = new URLSearchParams(search);
    const parsePositive = (value: string | null, fallback: number) => {
        const parsed = Number(value);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
    };
    const parseNumbers = (key: string) =>
        params
            .getAll(key)
            .map(value => Number(value))
            .filter(value => Number.isInteger(value));
    const query: CustomerInfoQuery = {
        page: parsePositive(params.get("page"), 1),
        size: parsePositive(params.get("size"), 10)
    };
    const param = params.get("param");
    const memberLevel = parseNumbers("memberLevel");
    const state = parseNumbers("state");
    if (param) query.param = param;
    if (memberLevel.length) query.memberLevel = memberLevel;
    if (params.has("memberState")) query.memberState = Number(params.get("memberState"));
    if (params.has("isContact")) query.isContact = params.get("isContact") === "true";
    if (state.length) query.state = state;
    for (const key of [
        "sortKey",
        "sortOrder",
        "registeredBeginTime",
        "registeredEndTime",
        "activeBeginTime",
        "activeEndTime",
        "expiryBeginDate",
        "expiryEndDate"
    ] as const) {
        const value = params.get(key);
        if (value) query[key] = value;
    }
    return query;
}

const memberLevelLabels: Record<number, string> = { 1: "非会员", 2: "基础会员", 3: "高级会员" };
const memberStateLabels: Record<number, string> = { 1: "激活", 2: "过期" };
const userStateLabels: Record<number, string> = { 0: "已禁用", 1: "已启用", 2: "注销中", 3: "已注销" };
const CUSTOMER_EXPORT_LIMIT = 10000;
const EXPORT_LIMIT_MESSAGE = "导出失败，导出内容超过10000条，请筛选后重新导出";
const SELECT_ALL_LIMIT_MESSAGE = "勾选数据超过10000条，请调整筛选条件后重新操作";

const formatDateTime = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatDateOnly = (value?: string) => (value ? value.slice(0, 10) : DEFAULT_VALUE);

/** 将 zhihao 表格时间排序转换为旧 Vue 客户分页接口字段。 */
function getCustomerSortParams(sort: { prop?: string; order: "ascending" | "descending" | null }) {
    const sortKeyMap = {
        createdTime: "created_time",
        memberActiveTime: "member_active_time",
        memberExpiryDate: "member_expiry_date"
    } as const;
    const sortKey = sort.prop ? sortKeyMap[sort.prop as keyof typeof sortKeyMap] : undefined;
    if (!sortKey || !sort.order) return {};
    return { sortKey, sortOrder: sort.order === "ascending" ? "asc" : "desc" };
}

/** 复用 Vue `/admin/customer/paging` 用户分页接口。 */
export async function loadCustomerPage(query: CustomerInfoQuery) {
    const response = await request.service.get("/admin/customer/paging", { params: query });
    return response.data;
}

/** 复用旧 Vue 已选用户沟通记录统计接口。 */
export async function loadCustomerCommunicateCount(query: CustomerSelectedQuery): Promise<number> {
    const response = await request.service.get<number>("/admin/customer/communicate-record", { params: query });
    return Number(response.data || 0);
}

/** 保留 Vue 用户启用/禁用路径和状态参数。 */
export async function updateCustomerState(id: number, state: number) {
    const response = await request.service.put(`/admin/customer/update/${id}/${state}`);
    return response.data;
}

/** 保留 Vue 已注销用户恢复接口。 */
export async function restoreCustomer(id: number) {
    const response = await request.service.put(`/admin/customer/restore/${id}`);
    return response.data;
}

/** 保留 Vue 用户列表导出接口，查询参数沿用当前分页筛选条件。 */
export async function exportCustomerList(query: CustomerSelectedQuery) {
    return request.service.get("/admin/customer/exportCustomer", { params: query, responseType: "blob" });
}

/** 保留 Vue 用户沟通记录导出接口。 */
export async function exportCustomerCommunication(query: CustomerSelectedQuery) {
    return request.service.get("/admin/customer/export", { params: query, responseType: "blob" });
}

export function CustomerInfoPage({
    loadCustomers = loadCustomerPage,
    loadCommunicateCount = loadCustomerCommunicateCount,
    updateState = updateCustomerState,
    restore = restoreCustomer,
    exportCustomer = exportCustomerList,
    exportCommunication = exportCustomerCommunication,
    navigateTo
}: CustomerInfoPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const banButton = useUserStore(user => user.banButton);
    const [customers, setCustomers] = useState<CustomerInfoRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<CustomerInfoQuery>(() => parseCustomerInfoQuery(location.search));
    const [param, setParam] = useState(() => query.param || "");
    const [memberLevel, setMemberLevel] = useState<number[]>(() => query.memberLevel || []);
    const [memberState, setMemberState] = useState<number | undefined>(() => query.memberState);
    const [isContact, setIsContact] = useState<boolean | undefined>(() => query.isContact);
    const [state, setState] = useState<number[]>(() => query.state || []);
    const [createdDate, setCreatedDate] = useState<[string, string] | null>(() =>
        query.registeredBeginTime && query.registeredEndTime
            ? [query.registeredBeginTime.slice(0, 10), query.registeredEndTime.slice(0, 10)]
            : null
    );
    const [memberActiveDate, setMemberActiveDate] = useState<[string, string] | null>(() =>
        query.activeBeginTime && query.activeEndTime
            ? [query.activeBeginTime.slice(0, 10), query.activeEndTime.slice(0, 10)]
            : null
    );
    const [memberExpiryDate, setMemberExpiryDate] = useState<[string, string] | null>(() =>
        query.expiryBeginDate && query.expiryEndDate ? [query.expiryBeginDate, query.expiryEndDate] : null
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [exportWarningMessage, setExportWarningMessage] = useState("");
    const [exportLoading, setExportLoading] = useState(false);
    const [communicationExportLoading, setCommunicationExportLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [excludeIds, setExcludeIds] = useState<number[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const tableRef = useRef<TableRef<CustomerInfoRecord>>(null);
    const syncingTableSelectionRef = useRef(false);
    const [communicateCount, setCommunicateCount] = useState(0);
    const [selectedDialogOpen, setSelectedDialogOpen] = useState(false);

    useEffect(() => {
        const nextSearch = serializeCustomerInfoQuery(query);
        const currentSearch = location.search.startsWith("?") ? location.search.slice(1) : location.search;
        if (nextSearch === currentSearch) return;
        navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" }, { replace: true });
    }, [location.pathname, location.search, navigate, query]);

    /** 已选弹窗允许缺省分页；调用主分页函数前补齐旧接口必填的页码与条数。 */
    const loadSelectedPage = useCallback(
        (selectedQuery: CustomerSelectedQuery) =>
            loadCustomers({ page: selectedQuery.page ?? 1, size: selectedQuery.size ?? 10, ...selectedQuery }),
        [loadCustomers]
    );

    useEffect(() => {
        let disposed = false;
        void loadCustomers(query)
            .then(result => {
                if (disposed) return;
                setCustomers(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setCustomers([]);
                setTotal(0);
                setErrorMessage("用户列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCustomers, query]);

    useEffect(() => {
        const table = tableRef.current;
        if (!table) return;

        // TableRef 的 toggleRowSelection 闭包只读取当前渲染的选中 keys；先比较再批量全选，避免循环调用只留下最后一行。
        const desiredRows = customers.filter(row =>
            selectAll ? !excludeIds.includes(row.customer.id) : selectedIds.includes(row.customer.id)
        );
        const currentIds = new Set(table.getSelectionRows().map(row => row.customer.id));
        const desiredIds = new Set(desiredRows.map(row => row.customer.id));
        const selectionMatches =
            currentIds.size === desiredIds.size && [...desiredIds].every(customerId => currentIds.has(customerId));
        if (selectionMatches) return;

        syncingTableSelectionRef.current = true;
        if (desiredRows.length === 0) {
            table.clearSelection();
            syncingTableSelectionRef.current = false;
            return;
        }
        if (desiredRows.length === customers.length) {
            // TableRef 按当前已选 keys 判断 toggleAllSelection 的方向；先清空并跨过一次 render，确保它一定选中整页。
            table.clearSelection();
            const timer = setTimeout(() => {
                tableRef.current?.toggleAllSelection();
                syncingTableSelectionRef.current = false;
            }, 0);
            return () => {
                clearTimeout(timer);
                syncingTableSelectionRef.current = false;
            };
        }

        // 部分选择需逐次跨过 React render，让 TableRef 每次使用最新快照；同步回调不应改写跨页逻辑选择。
        table.clearSelection();
        let nextRowIndex = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const selectNextRow = () => {
            const currentTable = tableRef.current;
            if (!currentTable || nextRowIndex >= desiredRows.length) {
                syncingTableSelectionRef.current = false;
                return;
            }
            currentTable.toggleRowSelection(desiredRows[nextRowIndex], true);
            nextRowIndex += 1;
            if (nextRowIndex < desiredRows.length) {
                timer = setTimeout(selectNextRow, 0);
            } else {
                syncingTableSelectionRef.current = false;
            }
        };
        timer = setTimeout(selectNextRow, 0);

        return () => {
            if (timer !== undefined) clearTimeout(timer);
            syncingTableSelectionRef.current = false;
        };
    }, [customers, excludeIds, selectAll, selectedIds]);

    // 选中范围变化时沿用 Vue 的统计接口，避免查看已选前缺少沟通记录数。
    useEffect(() => {
        const selectedCount = selectAll ? Math.max(total - excludeIds.length, 0) : selectedIds.length;
        if (selectedCount === 0) {
            setCommunicateCount(0);
            return;
        }
        // 统计与导出只使用搜索条件和选择范围，分页字段仅属于列表请求。
        const { page: _page, size: _size, ...searchQuery } = query;
        void _page;
        void _size;
        const selectedQuery: CustomerSelectedQuery = selectAll
            ? { ...searchQuery, selectAll: true, excludeIds }
            : { ...searchQuery, selectAll: false, selectedIds };
        void loadCommunicateCount(selectedQuery)
            .then(value => setCommunicateCount(Number(value) || 0))
            .catch(() => setCommunicateCount(0));
    }, [excludeIds, loadCommunicateCount, query, selectAll, selectedIds, total]);

    const runSearch = () => {
        setSelectedIds([]);
        setExcludeIds([]);
        setSelectAll(false);
        const dateQuery: Pick<
            CustomerInfoQuery,
            | "registeredBeginTime"
            | "registeredEndTime"
            | "activeBeginTime"
            | "activeEndTime"
            | "expiryBeginDate"
            | "expiryEndDate"
        > = {};
        // 旧 Vue 页面将完整日期范围转换为接口的起止边界字段。
        if (createdDate?.[0] && createdDate[1]) {
            dateQuery.registeredBeginTime = `${createdDate[0]}T00:00:00`;
            dateQuery.registeredEndTime = `${createdDate[1]}T23:59:59`;
        }
        if (memberActiveDate?.[0] && memberActiveDate[1]) {
            dateQuery.activeBeginTime = `${memberActiveDate[0]}T00:00:00`;
            dateQuery.activeEndTime = `${memberActiveDate[1]}T23:59:59`;
        }
        if (memberExpiryDate?.[0] && memberExpiryDate[1]) {
            dateQuery.expiryBeginDate = memberExpiryDate[0];
            dateQuery.expiryEndDate = memberExpiryDate[1];
        }
        setQuery({
            page: 1,
            size: 10,
            ...(param ? { param } : {}),
            ...(memberLevel.length ? { memberLevel } : {}),
            ...(memberState ? { memberState } : {}),
            ...(isContact !== undefined ? { isContact } : {}),
            ...(state.length ? { state } : {}),
            ...dateQuery
        });
    };

    const goToCustomer = (id: number) => {
        if (navigateTo) {
            navigateTo(id);
            return;
        }
        navigate(`/lianYunUser/userInfo?userId=${id}`);
    };

    const handleState = async (row: CustomerInfoRecord) => {
        const customerId = row.customer.id;
        const currentState = Number(row.customer.state);
        try {
            if (currentState === 3) {
                await restore(customerId);
                notifyOperationSuccess("已恢复");
            } else {
                await updateState(customerId, currentState === 1 ? 0 : 1);
                notifyOperationSuccess(currentState === 1 ? "已禁用" : "已启用");
            }
            setQuery(current => ({ ...current }));
        } catch (error) {
            showRequestError(error, "用户状态操作失败");
        }
    };

    /** 对齐旧 Vue formatter：申请机构名称优先，其次是客户机构名称和个人实名。 */
    const renderName = (row: CustomerInfoRecord) =>
        row.institutionApply?.institutionName || row.customer.institutionName || row.customer.realName || DEFAULT_VALUE;

    const selectedCount = selectAll ? Math.max(total - excludeIds.length, 0) : selectedIds.length;
    /** 将当前选中范围合并到导出请求，保持 Vue 的 selectAll/selectedIds/excludeIds payload。 */
    const getExportQuery = (): CustomerSelectedQuery => {
        const { page: _page, size: _size, ...searchQuery } = query;
        void _page;
        void _size;
        return selectAll ? { ...searchQuery, selectAll: true, excludeIds } : { ...searchQuery, selectedIds };
    };

    /** 按浏览器实际响应和兼容测试替身统一取出导出文件。 */
    const getExportBlob = (result: unknown) => {
        if (result instanceof Blob) return result;
        if (result && typeof result === "object" && "data" in result && result.data instanceof Blob) {
            return result.data;
        }
        throw new Error("导出接口未返回文件");
    };

    /** 将导出 Blob 交给浏览器下载，并及时释放本次创建的对象地址。 */
    const downloadExportBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    /** 生成旧 Vue `YYYYMMDDHHmmss` 格式的下载文件名。 */
    const getExportFilename = (prefix: string) => {
        const now = new Date();
        const pad = (value: number) => String(value).padStart(2, "0");
        const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        return `${prefix}${timestamp}.xlsx`;
    };

    /** 对齐旧 Vue 的导出数量限制与下载反馈，超量时不触发导出接口。 */
    const handleExport = async (
        exporter: (exportQuery: CustomerSelectedQuery) => Promise<unknown>,
        exportCount: number,
        filenamePrefix: string,
        setLoading: (loading: boolean) => void
    ) => {
        if (exportCount > CUSTOMER_EXPORT_LIMIT) {
            setExportWarningMessage(EXPORT_LIMIT_MESSAGE);
            return;
        }
        setExportWarningMessage("");
        setLoading(true);
        try {
            const result = await exporter(getExportQuery());
            downloadExportBlob(getExportBlob(result), getExportFilename(filenamePrefix));
            notifyOperationSuccess("导出成功");
        } catch (error) {
            showRequestError(error, "导出失败");
        } finally {
            setLoading(false);
        }
    };

    /** 对齐旧 Vue 的全选前置校验，避免将超过上限的结果写入 selectAll 状态。 */
    const toggleSelectAll = () => {
        if (!selectAll && total > CUSTOMER_EXPORT_LIMIT) {
            setExportWarningMessage(SELECT_ALL_LIMIT_MESSAGE);
            return;
        }
        setExportWarningMessage("");
        setSelectAll(current => !current);
        setSelectedIds([]);
        setExcludeIds([]);
    };

    const updateSelection = (selection: CustomerInfoRecord[]) => {
        if (syncingTableSelectionRef.current) return;
        const pageIds = customers.map(row => row.customer.id);
        const checkedIds = selection.map(row => row.customer.id);
        if (selectAll) {
            setExcludeIds(current => [
                ...current.filter(id => !pageIds.includes(id)),
                ...pageIds.filter(id => !checkedIds.includes(id))
            ]);
            return;
        }
        setSelectedIds(current => [...current.filter(id => !pageIds.includes(id)), ...checkedIds]);
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 客户列表的嵌套字段、状态映射和操作权限。
    const columns: TableColumnProps<CustomerInfoRecord>[] = [
        { key: "selection", type: "selection", label: "选择", prop: "customer" },
        {
            key: "id",
            label: "用户 ID",
            width: 180,
            prop: "customer",
            render: ({ row }) => (
                <Button
                    type="link"
                    aria-label={`查看 ${row.customer.code || row.customer.id}`}
                    onClick={() => goToCustomer(row.customer.id)}
                >
                    {row.customer.code || row.customer.id}
                </Button>
            )
        },
        {
            key: "name",
            label: "个人实名/机构名称",
            minWidth: 160,
            prop: "customer",
            render: ({ row }) => renderName(row)
        },
        {
            key: "mobile",
            label: "注册手机号",
            width: 140,
            prop: "customer",
            render: ({ row }) => row.customer.mobile || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "注册时间",
            width: 150,
            prop: "createdTime",
            sortable: true,
            render: ({ row }) => formatDateTime(row.customer.createdTime)
        },
        {
            key: "memberLevel",
            label: "会员等级",
            width: 150,
            prop: "customer",
            render: ({ row }) => memberLevelLabels[Number(row.customer.memberLevel)] || DEFAULT_VALUE
        },
        {
            key: "memberActiveTime",
            label: "会员激活时间",
            width: 150,
            prop: "memberActiveTime",
            sortable: true,
            render: ({ row }) => formatDateTime(row.customer.memberActiveTime)
        },
        {
            key: "memberExpiryDate",
            label: "会员到期日期",
            width: 150,
            prop: "memberExpiryDate",
            sortable: true,
            render: ({ row }) => formatDateOnly(row.customer.memberExpiryDate)
        },
        {
            key: "memberState",
            label: "会员状态",
            width: 100,
            prop: "customer",
            render: ({ row }) => memberStateLabels[Number(row.customer.memberState)] || DEFAULT_VALUE
        },
        {
            key: "isConcat",
            label: "是否电联",
            width: 100,
            prop: "isConcat",
            render: ({ row }) => (row.isConcat ? "是" : "否")
        },
        {
            key: "latestCommunicateSummary",
            label: "最新沟通总结",
            width: 190,
            prop: "latestCommunicateSummary",
            render: ({ row }) => row.latestCommunicateSummary || DEFAULT_VALUE
        },
        {
            key: "state",
            label: "用户状态",
            width: 100,
            prop: "customer",
            render: ({ row }) => userStateLabels[Number(row.customer.state)] || DEFAULT_VALUE
        },
        {
            key: "operation",
            label: "操作",
            // 保留操作按钮完整点击区，避免固定列裁切状态切换文案。
            width: 180,
            prop: "customer",
            fixed: "right",
            render: ({ row }) => {
                const currentState = Number(row.customer.state);
                const actionLabel = currentState === 3 ? "恢复" : currentState === 1 ? "禁用" : "启用";
                const canToggle = currentState === 1 || currentState === 0;
                if (!canToggle && currentState !== 3) return null;
                return (
                    <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                        <Button
                            type="link"
                            aria-label={`${actionLabel} ${row.customer.code || row.customer.id}`}
                            disabled={banButton}
                            onClick={() => void handleState(row)}
                        >
                            {actionLabel}
                        </Button>
                    </Permission>
                );
            }
        }
    ];

    return (
        <section aria-label="customer-info-page" className="customer-info-page flex h-full min-h-full flex-col">
            <Card>
                <AdminDetailTable<CustomerInfoRecord>
                    ref={tableRef}
                    data={customers}
                    columns={columns}
                    // 旧 Vue Element 表格用 auto 布局，让未指定宽度的实名/机构名称列填充剩余空间。
                    tableLayout="auto"
                    pagination={false}
                    rowKey={row => String(row.customer.id)}
                    onSelectionChange={updateSelection}
                    onSortChange={sort =>
                        setQuery(current => {
                            const nextQuery = { ...current, page: 1 };
                            delete nextQuery.sortKey;
                            delete nextQuery.sortOrder;
                            return { ...nextQuery, ...getCustomerSortParams(sort) };
                        })
                    }
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            {exportWarningMessage ? (
                                <Alert className="mb-4" role="alert" title={exportWarningMessage} type="warning" />
                            ) : null}

                            <Space className="admin-table-filter-form mb-4" wrap>
                                <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                                    <Button
                                        aria-label="导出"
                                        disabled={banButton}
                                        loading={exportLoading}
                                        type="primary"
                                        onClick={() =>
                                            void handleExport(
                                                exportCustomer,
                                                selectedCount,
                                                "用户列表",
                                                setExportLoading
                                            )
                                        }
                                    >
                                        导出
                                    </Button>
                                </Permission>
                                <Permission anyOf={["ROLE_SS_CUSTOMER_W"]}>
                                    <Button
                                        aria-label="导出沟通记录"
                                        disabled={banButton}
                                        loading={communicationExportLoading}
                                        type="primary"
                                        onClick={() =>
                                            void handleExport(
                                                exportCommunication,
                                                communicateCount,
                                                "用户沟通记录",
                                                setCommunicationExportLoading
                                            )
                                        }
                                    >
                                        导出沟通记录
                                    </Button>
                                </Permission>
                                <label>
                                    用户关键词
                                    <Input
                                        aria-label="用户关键词"
                                        placeholder="请输入用户ID、手机号、个人实名、机构名称"
                                        value={param}
                                        onChange={event => setParam(event.target.value)}
                                    />
                                </label>
                                <ZhDateRangePicker
                                    clearable
                                    endPlaceholder="注册结束时间"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="注册开始时间"
                                    type="daterange"
                                    value={createdDate}
                                    valueFormat="YYYY-MM-DD"
                                    onChange={(_value, formattedValue) => setCreatedDate(formattedValue)}
                                />
                                <label>
                                    会员等级
                                    <Select
                                        allowClear
                                        aria-label="会员等级"
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={Object.entries(memberLevelLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择会员等级"
                                        virtual={false}
                                        value={memberLevel}
                                        onChange={setMemberLevel}
                                    />
                                </label>
                                <ZhDateRangePicker
                                    clearable
                                    endPlaceholder="激活结束时间"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="激活开始时间"
                                    type="daterange"
                                    value={memberActiveDate}
                                    valueFormat="YYYY-MM-DD"
                                    onChange={(_value, formattedValue) => setMemberActiveDate(formattedValue)}
                                />
                                <ZhDateRangePicker
                                    clearable
                                    endPlaceholder="到期结束日期"
                                    format="YYYY-MM-DD"
                                    rangeSeparator="-"
                                    startPlaceholder="到期开始日期"
                                    type="daterange"
                                    value={memberExpiryDate}
                                    valueFormat="YYYY-MM-DD"
                                    onChange={(_value, formattedValue) => setMemberExpiryDate(formattedValue)}
                                />
                                <label>
                                    会员状态
                                    <Select
                                        allowClear
                                        aria-label="会员状态"
                                        options={Object.entries(memberStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择会员状态"
                                        value={memberState}
                                        onChange={setMemberState}
                                    />
                                </label>
                                <label>
                                    是否电联
                                    <Select
                                        allowClear
                                        aria-label="是否电联"
                                        options={[
                                            { label: "是", value: true },
                                            { label: "否", value: false }
                                        ]}
                                        placeholder="请选择是否电联"
                                        value={isContact}
                                        onChange={setIsContact}
                                    />
                                </label>
                                <label>
                                    用户状态
                                    <Select
                                        allowClear
                                        aria-label="用户状态"
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={Object.entries(userStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择状态"
                                        value={state}
                                        onChange={setState}
                                    />
                                </label>
                                <Button
                                    aria-label="查询"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                    onClick={runSearch}
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={() => {
                                        setParam("");
                                        setMemberLevel([]);
                                        setMemberState(undefined);
                                        setIsContact(undefined);
                                        setState([]);
                                        setCreatedDate(null);
                                        setMemberActiveDate(null);
                                        setMemberExpiryDate(null);
                                        setQuery(current => ({ page: 1, size: current.size }));
                                    }}
                                />
                            </Space>

                            <Space className="mb-4" wrap>
                                <Button aria-label={selectAll ? "取消全部" : "选择全部"} onClick={toggleSelectAll}>
                                    {selectAll ? "取消全部" : "选择全部"}
                                </Button>
                                <Button
                                    aria-label={`查看已选${selectedCount ? `(${selectedCount})` : ""}`}
                                    disabled={selectedCount === 0}
                                    onClick={() => setSelectedDialogOpen(true)}
                                >
                                    查看已选{selectedCount ? `(${selectedCount})` : ""}
                                </Button>
                                {selectedCount ? (
                                    <Typography.Text>已选沟通记录数：{communicateCount}条</Typography.Text>
                                ) : null}
                                <Typography.Text type="secondary">
                                    <span aria-hidden="true" className="iconfont icon-a-Frame38 mr-1" />
                                    点击“选择全部”前建议先进行筛选，点击后将勾选此筛选条件下所有的用户
                                </Typography.Text>
                            </Space>
                        </div>
                    }
                    append={
                        total > 0 ? (
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={query.page}
                                    pageSize={query.size}
                                    total={total}
                                    // Vue Element Plus 默认展示 6 个连续页码；Ant 的默认分页少一个，首屏补齐第 6 页并保留可点击行为。
                                    itemRender={(page, type, defaultElement) =>
                                        type === "jump-next" && query.page === 1 && page === 6 ? (
                                            <span className="customer-info-page__jump-next-pages">
                                                <button
                                                    aria-label={`第${page}页`}
                                                    className="customer-info-page__page-item"
                                                    type="button"
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        setQuery(current => ({ ...current, page }));
                                                    }}
                                                >
                                                    {page}
                                                </button>
                                                {defaultElement}
                                            </span>
                                        ) : (
                                            defaultElement
                                        )
                                    }
                                    onChange={(page, pageSize) =>
                                        setQuery(current => ({
                                            ...current,
                                            page: pageSize !== current.size ? 1 : page,
                                            size: pageSize
                                        }))
                                    }
                                />
                            </div>
                        ) : null
                    }
                />
            </Card>
            <CustomerSelectedDialog
                open={selectedDialogOpen}
                query={
                    selectAll
                        ? (() => {
                              const { page: _page, size: _size, ...searchQuery } = query;
                              void _page;
                              void _size;
                              return { ...searchQuery, selectAll: true, excludeIds };
                          })()
                        : (() => {
                              const { page: _page, size: _size, ...searchQuery } = query;
                              void _page;
                              void _size;
                              return { ...searchQuery, selectAll: false, selectedIds };
                          })()
                }
                selectCount={selectedCount}
                communicateCount={communicateCount}
                loadCustomers={loadSelectedPage}
                onNavigate={goToCustomer}
                onCancel={() => setSelectedDialogOpen(false)}
            />
        </section>
    );
}
